import { Inject, Injectable } from '@angular/core';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, User } from '@vendasta/iam';
import { Salesperson } from '@vendasta/sales';
import { Observable, map } from 'rxjs';

export interface Person {
  firstName?: string;
  lastName?: string;
  email: string;
}
export function getFormattedDisplayName(found: Person): string {
  let name = found?.firstName;
  if (found?.lastName) {
    if (name === '') {
      name = found?.lastName;
    } else {
      name += ' ' + found?.lastName;
    }
  }
  if (!name) {
    name = found?.email;
  }
  return name;
}

export const NONE_ASSIGNED = '';
export const ASSIGNED_SALESPERSON = 'ASSIGNED_SALESPERSON';
export const ADDITIONAL_SALESPEOPLE = 'ADDITIONAL_SALESPEOPLE';
export const ASSIGNED_FULFILLMENT_AGENT = 'ASSIGNED_FULFILLMENT_AGENT';
export const ASSIGNED_SMBS = 'ASSIGNED_SMBS';

// All agents have a first name, last name, and some form of identifier.
export type Agent = Salesperson | User;

// Users can be accessed either as an IAM user or the indicated type (e.g. Salesperson)

@Injectable({ providedIn: 'root' })
export class UsersService {
  constructor(
    private readonly iamService: IAMService,
    private readonly translateService: TranslateService,
    private readonly snackbarService: SnackbarService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) private readonly namespace$: Observable<string>,
  ) {}

  getUsersById(userIds: string[]): Observable<User[]> {
    return this.iamService.getMultiUsers(userIds.map((id) => ({ userId: id })));
  }

  getUserDisplay(users: Agent[], userId: string): string {
    const found = users.find((u) => this.getId(u) === userId);
    if (!found) {
      return '';
    }
    return getFormattedDisplayName(found);
  }

  getUserDisplayAgnosticOfPartner(userId: string): Observable<string> {
    return this.iamService.getMultiUsers([{ userId: userId }]).pipe(map((users) => this.getUserDisplay(users, userId)));
  }

  getUsersDisplayAgnosticOfPartner(userIds: string[]): Observable<string> {
    return this.iamService.getMultiUsers(userIds.map((userId) => ({ userId: userId }))).pipe(
      map((users) => {
        const names = users.map((user) => getFormattedDisplayName(user));
        return names.join(', ');
      }),
    );
  }

  getId(agent: Agent): string {
    if ('salespersonId' in agent) {
      return agent.salespersonId;
    } else {
      return agent.userId;
    }
  }
}
